import {
  Equipment,
  Expense,
  type ExpenseCategory,
  ExpenseList,
  OkResponse,
  RecurringExpense,
} from '@deizi/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { api } from './api';

export const expensesKey = ['expenses'] as const;

export function useExpenses(month: string) {
  return useQuery({
    queryKey: [...expensesKey, 'month', month],
    queryFn: () => api.get(`/expenses?month=${month}`, ExpenseList),
    placeholderData: (prev) => prev,
  });
}

export const useRecurring = () =>
  useQuery({
    queryKey: [...expensesKey, 'recurring'],
    queryFn: () => api.get('/recurring-expenses', z.object({ data: z.array(RecurringExpense) })),
  });

export const useEquipment = () =>
  useQuery({
    queryKey: [...expensesKey, 'equipment'],
    queryFn: () => api.get('/equipment', z.object({ data: z.array(Equipment) })),
  });

/** Expenses move reports too; purchases show their expense. */
export function useInvalidateExpenses() {
  const qc = useQueryClient();
  return () => {
    for (const key of [expensesKey, ['reports'], ['alerts'], ['purchases']]) {
      void qc.invalidateQueries({ queryKey: key });
    }
  };
}

export const expenseApi = {
  remove: (id: string) => api.delete(`/expenses/${id}`, OkResponse),
  restore: (id: string) => api.post(`/expenses/${id}/restore`, Expense),
  attach: (id: string, fileId: string) => api.post(`/expenses/${id}/files`, Expense, { fileId }),
};

/** Categories a recurring item usually has (board), the rest stay available. */
export const RECURRING_CATEGORIES: ExpenseCategory[] = [
  'software',
  'ads',
  'taxes',
  'other',
  'maintenance',
];
